<template>
  <div class="block">
    <SectionHeading
      :title="$t('Вложения')"
      :description="
        !templateView
          ? $t('Выберите допустимые типы документов для загрузки вложений')
          : ''
      "
    />

    <template v-if="templateView">
      <ViewGroupList :groups="viewGroups" />
    </template>

    <div v-else class="flex flex-col gap-8">
      <div class="flex flex-col gap-4 w-full">
        <!-- ФЛ -->
        <div class="rounded-xl flex flex-col">
          <div
            class="gray-block"
            @click="toggleExpand('document_fl_type_codes')"
          >
            <Checkbox
              :model-value="isGroupChecked('document_fl_type_codes')"
              :disabled="templateView"
              @click.stop
              @change="toggleGroup('document_fl_type_codes')"
            >
              <span class="font-medium text-sm text-foreground !gap-3">
                {{ $t("ФЛ") }}
              </span>
            </Checkbox>

            <svg-icon
              :name="
                expandedGroups['document_fl_type_codes']
                  ? 'huge-up'
                  : 'huge-down'
              "
              class="w-5 h-5 ml-auto text-muted-foreground transition-transform"
            />
          </div>

          <transition name="slide-fade">
            <div
              v-show="expandedGroups['document_fl_type_codes']"
              class="pl-8 flex flex-col gap-1 mt-3"
            >
              <div class="flex flex-col gap-2.5">
                <Checkbox
                  v-for="item in participant_types"
                  :key="item.code"
                  :model-value="
                    form.document_fl_type_codes?.includes(item.code)
                  "
                  :disabled="templateView"
                  @change="toggleItem('document_fl_type_codes', item.code)"
                  class="flex items-center gap-3"
                >
                  <span
                    class="flex items-center p-0 text-muted-foreground text-sm"
                  >
                    {{ item.name }}
                  </span>
                </Checkbox>
              </div>
            </div>
          </transition>
        </div>

        <!-- ЮЛ -->
        <div class="rounded-xl flex flex-col">
          <div
            class="gray-block"
            @click="toggleExpand('document_ul_type_codes')"
          >
            <Checkbox
              :model-value="isGroupChecked('document_ul_type_codes')"
              :disabled="templateView"
              @click.stop
              @change="toggleGroup('document_ul_type_codes')"
            >
              <span class="font-medium text-sm text-foreground !gap-3">
                {{ $t("ЮЛ") }}
              </span>
            </Checkbox>

            <svg-icon
              :name="
                expandedGroups['document_ul_type_codes']
                  ? 'huge-up'
                  : 'huge-down'
              "
              class="w-5 h-5 ml-auto text-muted-foreground transition-transform"
            />
          </div>

          <transition name="slide-fade">
            <div
              v-show="expandedGroups['document_ul_type_codes']"
              class="pl-8 flex flex-col gap-1 mt-3"
            >
              <div class="flex flex-col gap-2.5">
                <Checkbox
                  v-for="item in participant_types"
                  :key="item.code"
                  :model-value="
                    form.document_ul_type_codes?.includes(item.code)
                  "
                  :disabled="templateView"
                  @change="toggleItem('document_ul_type_codes', item.code)"
                  class="flex items-center gap-3"
                >
                  <span
                    class="flex items-center p-0 text-muted-foreground text-sm"
                  >
                    {{ item.name }}
                  </span>
                </Checkbox>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import ViewGroupList from "@/shared/ui/custom/ViewGroupList.vue";
import { getDirectoryItem } from "@/entities/directory";
import Checkbox from "@/shared/ui/checkbox/Checkbox.vue";
import { SectionHeading } from "@/shared/ui/section-heading";
import { useGroupCheckboxes } from "@/shared/lib/composables/useGroupCheckboxes";

const props = defineProps({
  form: {
    type: Object,
    default: () => ({}),
  },
});

const route = useRoute();
const { t } = useI18n();

const participant_types = ref([]);

const templateView = computed(
  () => route.name === "constructor_action_item_view"
);

const {
  expandedGroups,
  isGroupChecked,
  toggleGroup,
  toggleItem,
  toggleExpand,
} = useGroupCheckboxes(props.form, participant_types, templateView);

const viewGroups = computed(() => {
  const flCodes = props.form.document_fl_type_codes || [];
  const ulCodes = props.form.document_ul_type_codes || [];
  return [
    {
      title: t("Физическое лицо"),
      items: participant_types.value.filter((i) => flCodes.includes(i.code)),
    },
    {
      title: t("Юридическое лицо"),
      items: participant_types.value.filter((i) => ulCodes.includes(i.code)),
    },
  ];
});

async function fetchDocumentTypes() {
  try {
    const data = await getDirectoryItem("types_of_documents");

    if (data?.success && data?.payload) {
      participant_types.value = data.payload.children || [];

      if (isGroupChecked("document_fl_type_codes")) {
        expandedGroups["document_fl_type_codes"] = true;
      }

      if (isGroupChecked("document_ul_type_codes")) {
        expandedGroups["document_ul_type_codes"] = true;
      }
    }
  } catch (error) {
    console.error("Ошибка загрузки getDirectoryItem:", error);
  }
}

function validate() {
  return Promise.resolve(true);
}

onMounted(() => {
  fetchDocumentTypes();
});

defineExpose({ validate });
</script>

<style scoped lang="scss">
.block {
  @apply p-8 h-full overflow-auto rounded-2xl flex flex-col gap-6 w-1/2;

  background-color: hsl(var(--background));
  border: 1px solid hsl(var(--border));
}

.gray-block {
  @apply w-full flex items-center gap-3 rounded-xl px-4 py-2 cursor-pointer transition-colors;

  background-color: hsl(var(--muted));

  &:hover {
    background-color: hsl(var(--muted));
  }
}

@media (max-width: 1400px) {
  .block {
    @apply w-full;
  }
}
</style>
